import { ReactNode, useEffect, useMemo, useState } from 'react';
import { readMachineStatus } from 'tf-pulse-shared';
import CalendarIcon from '../../assets/icons/health-calendar.svg?react';
import GaugeIcon from '../../assets/icons/health-gauge.svg?react';
import ProgramIcon from '../../assets/icons/health-program.svg?react';
import RuntimeIcon from '../../assets/icons/health-runtime.svg?react';
import GraphIcon from '../../assets/icons/pulse-alert.svg?react';
import { MachineHistoryGraph } from '../../components/dialogs/MachineHistoryGraph';
import {
  useGetMachineGroupedDataQuery,
  type Machine,
} from '../../gql/generated';
import { formatMetricName, formatUnit } from '../../utils/formatMetric';
import { formatStatusAge } from '../../utils/formatStatusAge';
import {
  EJECTOR_DIMENSION,
  ejectorSource,
  healthSignals,
  HEALTH_CATEGORY,
} from './machineDataSources';
import { MachineStatusBadge } from './MachineStatusBadge';
import { ScrollingBarChart } from './ScrollingBarChart';

const MINUTE_MS = 60_000;

/** The valve chart covers the last hour, refreshed every minute. */
const VALVE_WINDOW_MS = 60 * MINUTE_MS;
const VALVE_WINDOW_MINUTES = VALVE_WINDOW_MS / MINUTE_MS;

const activationRate = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 1,
});

/** Rounds a top value up to 1, 2 or 5 times a power of ten. */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 5, 10].find((s) => s * power >= value) ?? 10;
  return step * power;
}

function useMinuteNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), MINUTE_MS);
    return () => window.clearInterval(timer);
  }, []);
  return Math.floor(now / MINUTE_MS) * MINUTE_MS;
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className='size-caption text-primary tracking-[0.08em] uppercase'>
      {children}
    </h3>
  );
}

function SystemCard({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof RuntimeIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <li
      className='bg-surface-primary border-border-secondary flex min-w-0
        items-center gap-3 rounded-xl border p-4
        shadow-[0_4px_12px_rgba(0,0,0,0.03)]'
    >
      <Icon aria-hidden className='h-6 w-6 shrink-0' />
      <div className='flex min-w-0 flex-col'>
        <span className='size-p1 text-primary truncate font-bold'>
          {children}
        </span>
        <span className='size-caption text-secondary truncate'>{label}</span>
      </div>
    </li>
  );
}

/** The Health tab on the machine's own status, valve and health signals. */
export function MachineHealthDataTab({ machine }: { machine: Machine }) {
  const now = useMinuteNow();
  const status = readMachineStatus(machine.state).status;
  const lastData = machine.availableSignals?.lastTimeUtc
    ? new Date(machine.availableSignals.lastTimeUtc)
    : null;
  const ejectors = useMemo(() => ejectorSource(machine), [machine]);
  const signals = healthSignals(machine);

  const valveQuery = useGetMachineGroupedDataQuery({
    variables: {
      id: machine.id,
      signalCategory: ejectors?.category ?? '',
      signal: ejectors?.signal ?? '',
      dimensions: [EJECTOR_DIMENSION],
      overTime: false,
      startTime: new Date(now - VALVE_WINDOW_MS).toISOString(),
      endTime: new Date(now).toISOString(),
    },
    skip: !ejectors,
  });

  // Activations per minute for each valve, in valve order. EDMS numbers the
  // valves from 0; the chart labels them from 1.
  const valveRates = useMemo(() => {
    const items = (valveQuery.data ?? valveQuery.previousData)?.machineData
      ?.items;
    if (!items?.length) return null;
    return items
      .map((item) => ({
        index: Number(
          item.dimensions?.find((d) => d.name === EJECTOR_DIMENSION)?.value
        ),
        rate: (item.sum ?? 0) / VALVE_WINDOW_MINUTES,
      }))
      .filter((valve) => Number.isFinite(valve.index))
      .sort((a, b) => a.index - b.index)
      .map((valve) => valve.rate);
  }, [valveQuery.data, valveQuery.previousData]);

  const totalRate = valveRates?.reduce((sum, rate) => sum + rate, 0);
  const yMax = niceMax(Math.max(...(valveRates ?? [0])));
  const yTicks = [1, 0.75, 0.5, 0.25, 0].map((f) => Math.round(yMax * f));
  const xTicks = valveRates
    ? [
        1,
        ...Array.from(
          { length: Math.floor(valveRates.length / 16) },
          (_, i) => (i + 1) * 16
        ),
      ]
    : [];

  return (
    <div
      className='bg-surface-focus-support flex flex-col gap-6 px-4 pt-4 pb-10'
    >
      <section className='flex flex-col gap-3'>
        <SectionTitle>System</SectionTitle>
        <ul className='grid grid-cols-2 gap-2'>
          <SystemCard icon={GaugeIcon} label='Machine state'>
            <MachineStatusBadge status={status} />
          </SystemCard>
          <SystemCard icon={CalendarIcon} label='Last data'>
            {formatStatusAge(lastData, new Date(now)) ?? '–'}
          </SystemCard>
          <SystemCard icon={ProgramIcon} label='Valves'>
            {valveRates ? valveRates.length : '–'}
          </SystemCard>
          <SystemCard icon={RuntimeIcon} label='Valve activations/min'>
            {totalRate !== undefined ? activationRate.format(totalRate) : '–'}
          </SystemCard>
        </ul>
      </section>

      {valveRates && (
        <section className='flex flex-col gap-2'>
          <SectionTitle>Valve activations, last hour</SectionTitle>
          <ScrollingBarChart
            yTitle='Activations/min'
            xTitle='Valve number'
            yTicks={yTicks}
            xTicks={xTicks}
            barCount={valveRates.length}
            ariaLabel={valveRates
              .map(
                (rate, i) =>
                  `Valve ${i + 1}: ${activationRate.format(rate)} activations/min`
              )
              .join(', ')}
          >
            {valveRates.map((rate, i) => (
              <div
                key={i}
                title={`Valve ${i + 1}: ${activationRate.format(rate)}/min`}
                className='flex-1 rounded-t-sm bg-amber-400 transition-[height]
                  duration-700 ease-out motion-reduce:transition-none'
                style={{ height: `${(rate / yMax) * 100}%` }}
              />
            ))}
          </ScrollingBarChart>
        </section>
      )}

      {signals.length > 0 && (
        <section className='flex flex-col gap-4'>
          <SectionTitle>Health signals</SectionTitle>
          {signals.map((signal) => (
            <div
              key={signal.signal}
              className='bg-surface-primary border-border-secondary rounded-2xl
                border px-4 py-4'
            >
              <MachineHistoryGraph
                header={
                  <div className='flex min-w-0 items-center gap-2'>
                    <GraphIcon className='h-5 w-5 shrink-0' aria-hidden />
                    <h4 className='size-p2 text-primary truncate font-bold'>
                      {formatMetricName(signal.signal)}
                    </h4>
                  </div>
                }
                machines={[
                  {
                    id: machine.id,
                    displayName: machine.displayName || machine.machineId,
                  },
                ]}
                signalCategory={HEALTH_CATEGORY}
                signal={signal.signal}
                unit={formatUnit(signal.unit ?? undefined)}
              />
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
